import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

type CropImageSize = {
  w: number;
  h: number;
};

type CropPoint = {
  x: number;
  y: number;
};

type DisplayedImageState = {
  width: number;
  height: number;
  offsetX: number;
  offsetY: number;
};

const WHEEL_DELTA_PIXEL = 0;
const WHEEL_DELTA_LINE = 1;
const WHEEL_DELTA_PAGE = 2;
const MIN_SCALE = 1;
const MAX_SCALE = 3;
const WHEEL_SCALE_SENSITIVITY = 0.001;

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const round = (value: number, precision = 4) => {
  const multiplier = Math.pow(10, precision);
  return Math.round(value * multiplier) / multiplier;
};

@Component({
    selector: 'lib-tablo-face-cropper',
    templateUrl: './tablo-face-cropper.component.html',
    styleUrls: ['./tablo-face-cropper.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TabloFaceCropperComponent implements AfterViewInit {
  @ViewChild('viewport', { static: true })
  viewport!: ElementRef<HTMLElement>;

  @ViewChild('imageElement', { static: false })
  imageElement?: ElementRef<HTMLImageElement>;

  @Output() updatedEmitter = new EventEmitter<CropPoint>();
  @Output() scaleChanged = new EventEmitter<number>();

  isDragging = false;
  frameStyle: Record<string, string | number> = {};
  imageStyle: Record<string, string | number> = {};
  focusStyle: Record<string, string | number> = {};

  private _x: number | null = null;
  private _y: number | null = null;
  private _scale = 1;
  private _selectedPoint = false;
  private _url: SafeUrl | string | null = null;
  private _imageSize: CropImageSize = { w: 1200, h: 150 };
  private naturalImageSize: CropImageSize | null = null;
  private displayedImageState: DisplayedImageState = {
    width: 0,
    height: 0,
    offsetX: 0,
    offsetY: 0,
  };
  private activePointerId: number | null = null;
  private lastEmittedPoint: CropPoint | null = null;

  @Input() set selectedPoint(value: boolean) {
    this._selectedPoint = !!value;
    this.syncViewFromInputs();
  }

  get selectedPoint() {
    return this._selectedPoint;
  }

  @Input() set imageSize(value: CropImageSize) {
    if (value?.w && value?.h) {
      this._imageSize = value;
      this.syncViewFromInputs();
    }
  }

  get imageSize() {
    return this._imageSize;
  }

  @Input() set x(value: number) {
    this._x = this.parseNumber(value);
    this.syncViewFromInputs();
  }

  get x() {
    return this._x;
  }

  @Input() set y(value: number) {
    this._y = this.parseNumber(value);
    this.syncViewFromInputs();
  }

  get y() {
    return this._y;
  }

  @Input() set scale(value: number) {
    this._scale = this.getClampedScale(this.parseNumber(value) ?? 1);
  }

  get scale() {
    return this._scale;
  }

  @Input() set url(value: unknown) {
    this.naturalImageSize = null;
    this.lastEmittedPoint = null;
    if (typeof value === 'string') {
      this._url = this.sanitizer.bypassSecurityTrustUrl(value);
    } else {
      this._url = value as SafeUrl | null;
    }
    this.syncViewFromInputs();
  }

  get url() {
    return this._url;
  }

  constructor(
    private sanitizer: DomSanitizer,
    private hostRef: ElementRef<HTMLElement>,
  ) {}

  get showHint() {
    return !!this.url && !this.selectedPoint;
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.syncViewFromInputs());
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.syncViewFromInputs();
  }

  @HostListener('window:pointermove', ['$event'])
  onPointerMove(event: PointerEvent) {
    if (!this.isDragging || event.pointerId !== this.activePointerId) {
      return;
    }

    event.preventDefault();
    this.updatePointFromClientPosition(event.clientX, event.clientY, true);
  }

  @HostListener('window:pointerup', ['$event'])
  @HostListener('window:pointercancel', ['$event'])
  endDrag(event: PointerEvent) {
    if (event.pointerId !== this.activePointerId) {
      return;
    }

    this.releasePointerCapture();
  }

  onPointerDown(event: PointerEvent) {
    if (!this.url || event.button !== 0) {
      return;
    }

    if (!this.isPointerInsideImage(event.clientX, event.clientY)) {
      return;
    }

    this.activePointerId = event.pointerId;
    this.isDragging = true;
    this.viewport.nativeElement.setPointerCapture?.(event.pointerId);
    event.preventDefault();
    this.updatePointFromClientPosition(event.clientX, event.clientY, true);
  }

  onImageLoad() {
    if (!this.imageElement?.nativeElement?.naturalWidth) {
      return;
    }

    this.naturalImageSize = {
      w: this.imageElement.nativeElement.naturalWidth,
      h: this.imageElement.nativeElement.naturalHeight,
    };
    this.syncViewFromInputs();
  }

  onWheel(event: WheelEvent) {
    if (!this.url || !this.isPointerInsideImage(event.clientX, event.clientY)) {
      return;
    }

    event.preventDefault();

    const nextScale = this.getClampedScale(
      this.scale - this.normalizeWheelDelta(event) * WHEEL_SCALE_SENSITIVITY,
    );

    if (nextScale === this.scale) {
      return;
    }

    this._scale = nextScale;
    this.scaleChanged.emit(nextScale);
  }

  private syncViewFromInputs() {
    if (!this.viewport?.nativeElement || !this.hostRef?.nativeElement) {
      return;
    }

    const hostRect = this.hostRef.nativeElement.getBoundingClientRect();
    if (!hostRect.width || !hostRect.height) {
      return;
    }

    this.frameStyle = {
      width: `${round(hostRect.width, 2)}px`,
      height: `${round(hostRect.height, 2)}px`,
    };

    this.displayedImageState = this.getDisplayedImageState(hostRect);
    this.imageStyle = {
      width: `${round(this.displayedImageState.width, 2)}px`,
      height: `${round(this.displayedImageState.height, 2)}px`,
      transform: `translate(${round(
        this.displayedImageState.offsetX,
        2,
      )}px, ${round(this.displayedImageState.offsetY, 2)}px)`,
    };

    this.focusStyle = this.getFocusStyle();
  }

  private getDisplayedImageState(hostRect: DOMRect): DisplayedImageState {
    const availableWidth = Math.max(hostRect.width, 0);
    const availableHeight = Math.max(hostRect.height, 0);
    const sourceWidth = Math.max(
      this.naturalImageSize?.w ?? this.imageSize.w ?? 1,
      1,
    );
    const sourceHeight = Math.max(
      this.naturalImageSize?.h ?? this.imageSize.h ?? 1,
      1,
    );

    if (!availableWidth || !availableHeight) {
      return {
        width: 0,
        height: 0,
        offsetX: 0,
        offsetY: 0,
      };
    }

    const containScale = Math.min(
      availableWidth / sourceWidth,
      availableHeight / sourceHeight,
    );
    const width = sourceWidth * containScale;
    const height = sourceHeight * containScale;

    return {
      width,
      height,
      offsetX: (availableWidth - width) / 2,
      offsetY: (availableHeight - height) / 2,
    };
  }

  private getTargetPoint(): CropPoint {
    return {
      x: clamp(this.x ?? 50, 0, 100),
      y: clamp(this.y ?? 50, 0, 100),
    };
  }

  private getFocusStyle() {
    const point = this.getTargetPoint();

    return {
      left: `${round(
        this.displayedImageState.offsetX +
          (point.x / 100) * this.displayedImageState.width,
        2,
      )}px`,
      top: `${round(
        this.displayedImageState.offsetY +
          (point.y / 100) * this.displayedImageState.height,
        2,
      )}px`,
    };
  }

  private isPointerInsideImage(clientX: number, clientY: number) {
    const viewportRect = this.viewport.nativeElement.getBoundingClientRect();
    const relativeX = clientX - viewportRect.left;
    const relativeY = clientY - viewportRect.top;

    return (
      relativeX >= this.displayedImageState.offsetX &&
      relativeX <=
        this.displayedImageState.offsetX + this.displayedImageState.width &&
      relativeY >= this.displayedImageState.offsetY &&
      relativeY <=
        this.displayedImageState.offsetY + this.displayedImageState.height
    );
  }

  private updatePointFromClientPosition(
    clientX: number,
    clientY: number,
    clampToImage = false,
  ) {
    const viewportRect = this.viewport.nativeElement.getBoundingClientRect();
    if (!this.displayedImageState.width || !this.displayedImageState.height) {
      return;
    }

    const relativeX =
      clientX - viewportRect.left - this.displayedImageState.offsetX;
    const relativeY =
      clientY - viewportRect.top - this.displayedImageState.offsetY;

    if (
      !clampToImage &&
      (relativeX < 0 ||
        relativeX > this.displayedImageState.width ||
        relativeY < 0 ||
        relativeY > this.displayedImageState.height)
    ) {
      return;
    }

    const point = {
      x: round(
        (clamp(relativeX, 0, this.displayedImageState.width) /
          this.displayedImageState.width) *
          100,
      ),
      y: round(
        (clamp(relativeY, 0, this.displayedImageState.height) /
          this.displayedImageState.height) *
          100,
      ),
    };

    this._x = point.x;
    this._y = point.y;
    this._selectedPoint = true;
    this.focusStyle = this.getFocusStyle();

    if (
      this.lastEmittedPoint &&
      this.lastEmittedPoint.x === point.x &&
      this.lastEmittedPoint.y === point.y
    ) {
      return;
    }

    this.lastEmittedPoint = point;
    this.updatedEmitter.emit(point);
  }

  private parseNumber(value: unknown) {
    return typeof value === 'number' && !Number.isNaN(value) ? value : null;
  }

  private getClampedScale(value: number) {
    return round(clamp(value, MIN_SCALE, MAX_SCALE), 2);
  }

  private normalizeWheelDelta(event: WheelEvent) {
    switch (event.deltaMode) {
      case WHEEL_DELTA_LINE:
        return event.deltaY * 16;
      case WHEEL_DELTA_PAGE:
        return event.deltaY * this.viewport.nativeElement.clientHeight;
      default:
        return event.deltaY;
    }
  }

  private releasePointerCapture() {
    if (this.activePointerId !== null) {
      this.viewport.nativeElement.releasePointerCapture?.(this.activePointerId);
    }

    this.activePointerId = null;
    this.isDragging = false;
  }
}
